import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Monitor, Smartphone, FileText, CreditCard, Camera, MapPin, 
  Clock, Users, Truck, Shield, BarChart3, MessageSquare,
  Snowflake, CalendarCheck, Receipt, ExternalLink, CheckCircle,
  Zap, Globe, Lock
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const CRMPromoSection = ({ variant = 'fence' }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  const isPS = variant === 'ps';
  const portalUrl = isPS 
    ? 'https://cafps.mccsys.ca/portal-chooser' 
    : 'https://cafinc.mccsys.ca/portal-chooser';
  const storeUrl = 'https://mccsys.ca/store/central-alberta-fencing-inc-la24fw';

  const colors = {
    primary: '#456CAC',
    primaryLight: '#6B8DC4',
    primaryDark: '#3A5A91',
    text: isDark ? '#f1f5f9' : '#0f172a',
    textMuted: isDark ? '#94a3b8' : '#64748b',
    bg: isDark ? '#0f172a' : '#f8fafc',
    surface: isDark ? '#1e293b' : '#ffffff',
    border: isDark ? '#334155' : '#e2e8f0',
  };

  // Benefits for the company
  const companyBenefits = isPS ? [
    { icon: Clock, title: '24/7 Dispatch', desc: 'Round-the-clock job assignment and crew management' },
    { icon: MapPin, title: 'GPS Fleet Tracking', desc: 'Real-time vehicle locations and route optimization' },
    { icon: Camera, title: 'Photo Documentation', desc: 'Timestamped before/after photos for every job' },
    { icon: BarChart3, title: 'Job Costing', desc: 'Track profitability on every service call' },
    { icon: Snowflake, title: 'Storm Tracking', desc: 'Auto-dispatch and billing based on weather events' },
    { icon: Receipt, title: 'Auto-Invoicing', desc: 'Invoices generated automatically when work completes' },
  ] : [
    { icon: FileText, title: 'Material Calculator', desc: 'Instant quotes with accurate material takeoffs' },
    { icon: MapPin, title: 'Fence Planner', desc: 'Interactive map-based project planning' },
    { icon: BarChart3, title: 'Inventory Tracking', desc: 'Real-time stock levels across all products' },
    { icon: Receipt, title: 'QuickBooks Sync', desc: 'Two-way sync keeps books balanced automatically' },
    { icon: Users, title: 'Contractor Network', desc: 'Manage subcontractors and trade accounts' },
    { icon: Truck, title: 'Delivery Scheduling', desc: 'Optimize routes and track deliveries' },
  ];

  // Benefits for customers
  const customerBenefits = isPS ? [
    { icon: Monitor, title: 'Service History', desc: 'View all past services and documentation' },
    { icon: Camera, title: 'Photo Gallery', desc: 'Before/after photos of completed work' },
    { icon: MessageSquare, title: 'Request Services', desc: 'Submit service requests anytime online' },
    { icon: FileText, title: 'Insurance Records', desc: 'Download service records for claims' },
    { icon: CreditCard, title: 'Online Payments', desc: 'Pay invoices securely from your portal' },
    { icon: CalendarCheck, title: 'Site Check Reports', desc: 'Daily visit logs and inspection reports' },
  ] : [
    { icon: Monitor, title: 'Order Tracking', desc: 'Track your order from placement to delivery' },
    { icon: FileText, title: 'Quote History', desc: 'Access all past quotes and invoices' },
    { icon: CreditCard, title: 'Easy Payments', desc: 'Pay invoices online, set up payment plans' },
    { icon: Smartphone, title: 'Mobile Access', desc: 'Full portal access from any device' },
    { icon: MessageSquare, title: 'Direct Messaging', desc: 'Chat with our team directly in portal' },
    { icon: CalendarCheck, title: 'Delivery Updates', desc: 'Real-time notifications on your order' },
  ];

  // Benefits for vendors/contractors
  const vendorBenefits = isPS ? [
    { icon: FileText, title: 'Work Orders', desc: 'Receive and update job assignments digitally' },
    { icon: Clock, title: 'Time Tracking', desc: 'Log hours and submit timesheets' },
    { icon: Camera, title: 'Job Documentation', desc: 'Upload completion photos from the field' },
    { icon: MessageSquare, title: 'Team Communication', desc: 'Stay connected with dispatch' },
  ] : [
    { icon: Receipt, title: 'Purchase Orders', desc: 'Receive and manage POs digitally' },
    { icon: BarChart3, title: 'Pricing Updates', desc: 'Submit and manage product pricing' },
    { icon: FileText, title: 'Invoice Submission', desc: 'Submit invoices directly through portal' },
    { icon: MessageSquare, title: 'Direct Communication', desc: 'Streamlined vendor communication' },
  ];

  const platformStats = [
    { value: '150+', label: 'Features' },
    { value: '7', label: 'AI Autopilots' },
    { value: '24/7', label: 'Access' },
    { value: '99.9%', label: 'Uptime' },
  ];

  return (
    <section className="py-20" style={{ backgroundColor: colors.bg }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ backgroundColor: `${colors.primary}15`, border: `1px solid ${colors.primary}30` }}>
            <Zap className="w-4 h-4" style={{ color: colors.primary }} />
            <span className="text-sm font-medium" style={{ color: colors.primary }}>Powered by MCC Systems</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Your {isPS ? 'Property' : 'Project'}, <span style={{ color: colors.primary }}>Your Portal</span>
          </h2>
          <p className="text-lg max-w-3xl mx-auto" style={{ color: colors.textMuted }}>
            {isPS 
              ? 'Track services, view documentation, request work, and manage your commercial properties - all from one secure customer portal powered by our AI-driven operations platform.'
              : 'Track orders, access quotes, manage payments, and communicate with our team - all from one secure customer portal powered by our AI-driven operations platform.'}
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {platformStats.map((stat) => (
            <div key={stat.label} className="text-center p-6 rounded-xl" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <div className="text-3xl font-bold mb-1" style={{ color: colors.primary }}>{stat.value}</div>
              <div className="text-sm" style={{ color: colors.textMuted }}>{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Three Column Benefits */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {/* For Our Business */}
          <div className="rounded-2xl p-6" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${colors.primary}15` }}>
                <BarChart3 className="w-5 h-5" style={{ color: colors.primary }} />
              </div>
              <h3 className="text-lg font-semibold">How We Use It</h3>
            </div>
            <p className="text-sm mb-6" style={{ color: colors.textMuted }}>
              {isPS 
                ? 'MCC Systems powers our entire operation - from dispatch to invoicing, ensuring reliable service delivery.'
                : 'MCC Systems runs our supply chain - from inventory to deliveries, ensuring you get materials on time.'}
            </p>
            <ul className="space-y-4">
              {companyBenefits.map((benefit) => (
                <li key={benefit.title} className="flex items-start gap-3">
                  <benefit.icon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: colors.primary }} />
                  <div>
                    <span className="font-medium text-sm">{benefit.title}</span>
                    <p className="text-xs" style={{ color: colors.textMuted }}>{benefit.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* For Customers */}
          <div className="rounded-2xl p-6" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${colors.primary}15` }}>
                <Users className="w-5 h-5" style={{ color: colors.primary }} />
              </div>
              <h3 className="text-lg font-semibold">Customer Portal</h3>
            </div>
            <p className="text-sm mb-6" style={{ color: colors.textMuted }}>
              {isPS 
                ? 'Your dedicated portal to track services, view documentation, and manage your property maintenance.'
                : 'Your dedicated portal to track orders, view quotes, and manage your fencing projects.'}
            </p>
            <ul className="space-y-4">
              {customerBenefits.map((benefit) => (
                <li key={benefit.title} className="flex items-start gap-3">
                  <benefit.icon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: colors.primary }} />
                  <div>
                    <span className="font-medium text-sm">{benefit.title}</span>
                    <p className="text-xs" style={{ color: colors.textMuted }}>{benefit.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
            <a 
              href={portalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 text-sm font-medium hover:gap-3 transition-all"
              style={{ color: colors.primary }}
            >
              Access Customer Portal <ExternalLink className="w-4 h-4" />
            </a>
          </div>

          {/* For Vendors/Contractors */}
          <div className="rounded-2xl p-6" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${colors.primary}15` }}>
                <Truck className="w-5 h-5" style={{ color: colors.primary }} />
              </div>
              <h3 className="text-lg font-semibold">{isPS ? 'Contractor Portal' : 'Vendor Portal'}</h3>
            </div>
            <p className="text-sm mb-6" style={{ color: colors.textMuted }}>
              {isPS 
                ? 'Subcontractors receive work orders, update status, and communicate - keeping field ops running smooth.'
                : 'Our suppliers manage orders, update pricing, and streamline our supply chain through their portal.'}
            </p>
            <ul className="space-y-4">
              {vendorBenefits.map((benefit) => (
                <li key={benefit.title} className="flex items-start gap-3">
                  <benefit.icon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: colors.primary }} />
                  <div>
                    <span className="font-medium text-sm">{benefit.title}</span>
                    <p className="text-xs" style={{ color: colors.textMuted }}>{benefit.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Platform Highlights */}
        <div className="rounded-2xl p-8 lg:p-12" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-bold mb-4">AI-Powered Operations</h3>
              <p className="mb-6" style={{ color: colors.textMuted }}>
                {isPS 
                  ? 'Our operations run on MCC Systems - an AI-powered platform built for field service. From auto-dispatch during snow storms to photo documentation for insurance, every service is tracked, documented, and optimized.'
                  : 'Our operations run on MCC Systems - an AI-powered platform built for field service. From material calculations to delivery tracking, every order is managed efficiently from quote to completion.'}
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5" style={{ color: colors.primary }} />
                  <span className="text-sm">Canadian built & hosted (PIPEDA compliant)</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5" style={{ color: colors.primary }} />
                  <span className="text-sm">Bank-level encryption (AES-256)</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5" style={{ color: colors.primary }} />
                  <span className="text-sm">QuickBooks integrated for seamless accounting</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5" style={{ color: colors.primary }} />
                  <span className="text-sm">Mobile access from any device (PWA)</span>
                </li>
              </ul>
              <div className="flex flex-wrap gap-4">
                <a 
                  href={portalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-white transition-colors"
                  style={{ backgroundColor: colors.primary }}
                >
                  <Globe className="w-4 h-4" />
                  Access Portal
                </a>
                {!isPS && (
                  <a 
                    href={storeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors"
                    style={{ backgroundColor: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}
                  >
                    Shop Online
                  </a>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="p-6 rounded-xl text-center" style={{ backgroundColor: colors.bg }}>
                <Globe className="w-8 h-8 mx-auto mb-3" style={{ color: colors.primary }} />
                <h4 className="font-semibold mb-1">24/7 Portal Access</h4>
                <p className="text-xs" style={{ color: colors.textMuted }}>Access anytime, anywhere</p>
              </div>
              <div className="p-6 rounded-xl text-center" style={{ backgroundColor: colors.bg }}>
                <Lock className="w-8 h-8 mx-auto mb-3" style={{ color: colors.primary }} />
                <h4 className="font-semibold mb-1">Secure & Private</h4>
                <p className="text-xs" style={{ color: colors.textMuted }}>Enterprise-grade security</p>
              </div>
              <div className="p-6 rounded-xl text-center" style={{ backgroundColor: colors.bg }}>
                <Smartphone className="w-8 h-8 mx-auto mb-3" style={{ color: colors.primary }} />
                <h4 className="font-semibold mb-1">Mobile Ready</h4>
                <p className="text-xs" style={{ color: colors.textMuted }}>Works on any device</p>
              </div>
              <div className="p-6 rounded-xl text-center" style={{ backgroundColor: colors.bg }}>
                <Zap className="w-8 h-8 mx-auto mb-3" style={{ color: colors.primary }} />
                <h4 className="font-semibold mb-1">AI-Powered</h4>
                <p className="text-xs" style={{ color: colors.textMuted }}>Smart automation</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CRMPromoSection;
